import React from "react";
import { Helmet } from "react-helmet-async";
import { motion, Variants } from "framer-motion";
import {
  TreePine,
  Leaf,
  Globe,
  Wind,
  ThermometerSun,
  Sprout,
  Handshake,
} from "lucide-react";

const siteUrl = "https://www.sevaafoundation.com";
const pageUrl = `${siteUrl}/tree-plantation`;
const defaultImage = `${siteUrl}/og-blogs.jpg`;
const logoImage = `${siteUrl}/logo512.png`;

const pageSchema = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "@id": `${pageUrl}#webpage`,
  name: "Tree Plantation & Environmental Action | SEVAA Foundation",
  alternateName: "Healing The Earth - Tree Plantation and Climate Action",
  url: pageUrl,
  inLanguage: "en-IN",
  description:
    "SEVAA Foundation supports tree plantation, urban forests, sapling distribution, climate awareness and environmental action to fight air pollution, heatwaves and ecological imbalance.",
  isPartOf: {
    "@type": "WebSite",
    "@id": `${siteUrl}/#website`,
    name: "SEVAA Foundation",
    url: siteUrl,
  },
  publisher: {
    "@type": "NGO",
    "@id": `${siteUrl}/#ngo`,
    name: "SEVAA Foundation",
    alternateName: "Swadesh Sevaa Charitable Foundation",
    url: siteUrl,
    logo: {
      "@type": "ImageObject",
      url: logoImage,
    },
    image: defaultImage,
    slogan: "सेवा परमो धर्मः",
    areaServed: [
      { "@type": "Country", name: "India" },
      { "@type": "Place", name: "Jharkhand" },
      { "@type": "Place", name: "Chatra" },
      { "@type": "Place", name: "Pakur" },
    ],
  },
  about: [
    "Tree Plantation",
    "Environmental Action",
    "Climate Change Awareness",
    "Air Pollution Control",
    "Urban Forest",
    "Miyawaki Forest",
    "Sapling Distribution",
    "Native Tree Plantation",
    "Community Plantation",
    "CSR Tree Plantation",
    "Green India",
    "Sustainable Development"
  ],
  primaryImageOfPage: {
    "@type": "ImageObject",
    url: defaultImage,
    width: 1200,
    height: 630,
  },
};

const serviceSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  "@id": `${pageUrl}#service`,
  name: "Tree Plantation and Environmental Action Program",
  serviceType: "Environmental Protection and Tree Plantation",
  url: pageUrl,
  provider: {
    "@type": "NGO",
    "@id": `${siteUrl}/#ngo`,
    name: "SEVAA Foundation",
    alternateName: "Swadesh Sevaa Charitable Foundation",
    url: siteUrl,
  },
  areaServed: {
    "@type": "Country",
    name: "India",
  },
  description:
    "SEVAA Foundation organizes tree plantation drives, urban forest creation, sapling distribution, school awareness campaigns and environmental action programs for climate resilience.",
};

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "@id": `${pageUrl}#breadcrumb`,
  itemListElement: [
    {
      "@type": "ListItem",
      position: 1,
      name: "Home",
      item: `${siteUrl}/`,
    },
    {
      "@type": "ListItem",
      position: 2,
      name: "Tree Plantation",
      item: pageUrl,
    },
  ],
};

const TreePlantation: React.FC = () => {
  const staggerContainerVariants: Variants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const gridItemVariants: Variants = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  return (
    <>
      <Helmet>
        <title>Tree Plantation NGO | SEVAA Foundation</title>

        <meta
          name="title"
          content="Tree Plantation NGO | SEVAA Foundation"
        />

        <meta
          name="description"
          content="Join SEVAA Foundation's tree plantation mission for green drives, urban forests, sapling distribution, climate awareness and community environmental action."
        />

        <meta name="robots" content="index, follow, max-image-preview:large" />
        <meta name="author" content="SEVAA Foundation" />
        <meta name="theme-color" content="#0f766e" />
        <meta name="language" content="English, Hindi" />

        <link rel="canonical" href={pageUrl} />

        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="SEVAA Foundation" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content="Tree Plantation NGO | Environmental Action" />
        <meta property="og:description" content="Support tree plantation, urban forests, sapling distribution, environmental awareness and climate action programs by SEVAA Foundation." />
        <meta property="og:image" content={defaultImage} />
        <meta property="og:image:alt" content="SEVAA Foundation tree plantation and environmental action program" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:locale" content="en_IN" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Tree Plantation NGO | Plant Trees with SEVAA Foundation" />
        <meta name="twitter:description" content="Join SEVAA Foundation's green drive for tree plantation, climate awareness, urban forests and environmental protection." />
        <meta name="twitter:image" content={defaultImage} />
        <meta name="twitter:image:alt" content="SEVAA Foundation tree plantation and environmental action program" />

        <script type="application/ld+json">
          {JSON.stringify(pageSchema)}
        </script>

        <script type="application/ld+json">
          {JSON.stringify(serviceSchema)}
        </script>

        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>

      <main className="bg-slate-50 min-h-screen font-poppins pb-20">
        {/* 1. HERO SECTION */}
        <section
          aria-labelledby="tree-plantation-heading"
          className="relative w-full h-[500px] md:h-[600px]"
        >
          <div className="absolute inset-0">
            <img
              src="https://images.unsplash.com/photo-1542601906990-b4d3fb778b09?q=80&w=2013&auto=format&fit=crop"
              alt="SEVAA Foundation tree plantation and environmental action green drive"
              className="w-full h-full object-cover"
              loading="eager"
              fetchPriority="high"
            />

            <div className="absolute inset-0 bg-gradient-to-r from-emerald-900/90 to-black/60"></div>
          </div>

          <div className="relative h-full max-w-7xl mx-auto px-6 lg:px-8 flex flex-col justify-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="max-w-3xl"
            >
              <span className="inline-block py-1 px-3 rounded-full bg-orange-500/20 text-orange-400 font-bold text-xs uppercase tracking-widest mb-4 border border-orange-500/30">
                Healing The Earth
              </span>

              <h1
                id="tree-plantation-heading"
                className="text-4xl md:text-5xl lg:text-6xl font-black text-white tracking-tight leading-[1.1] mb-6"
              >
                Tree Plantation & <br />
                <span className="text-orange-400">
                  Environmental Action
                </span>
              </h1>

              <p className="text-emerald-50 text-base md:text-lg font-medium leading-relaxed mb-8 max-w-2xl opacity-90">
                Breathing life into our future. Join SEVAA Foundation&apos;s
                green drive to combat climate change, restore ecosystems and
                build a sustainable world for the next generation.
              </p>
            </motion.div>
          </div>
        </section>

        {/* 2. THE HARSH REALITY */}
        <section
          aria-labelledby="environment-reality-heading"
          className="max-w-7xl mx-auto px-6 lg:px-8 -mt-20 relative z-10 mb-20"
        >
          <h2 id="environment-reality-heading" className="sr-only">
            Environmental Reality and Climate Crisis in India
          </h2>

          <div className="grid md:grid-cols-3 gap-6">
            <motion.article
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-white p-8 rounded-3xl shadow-xl border border-slate-100 hover:shadow-2xl transition-shadow duration-300"
            >
              <Wind className="w-12 h-12 text-rose-500 mb-6" />

              <h3 className="text-4xl font-black text-slate-900 mb-2">
                1.6<span className="text-xl">M+</span>
              </h3>

              <p className="text-slate-600 font-medium text-sm leading-relaxed">
                Premature deaths in India are linked to severe air pollution
                annually, making clean air, green cover and environmental
                protection urgent needs.
              </p>
            </motion.article>

            <motion.article
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-white p-8 rounded-3xl shadow-xl border border-slate-100 hover:shadow-2xl transition-shadow duration-300"
            >
              <Sprout className="w-12 h-12 text-emerald-500 mb-6" />

              <h3 className="text-4xl font-black text-slate-900 mb-2">
                24<span className="text-xl">%</span>
              </h3>

              <p className="text-slate-600 font-medium text-sm leading-relaxed">
                Current forest cover in India still falls short of the 33%
                target needed for ecological balance, climate resilience and
                long-term sustainability.
              </p>
            </motion.article>

            <motion.article
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="bg-emerald-800 p-8 rounded-3xl shadow-xl border border-emerald-700 text-white hover:shadow-2xl transition-shadow duration-300"
            >
              <ThermometerSun className="w-12 h-12 text-emerald-300 mb-6" />

              <h3 className="text-2xl font-black mb-2">Climate Crisis</h3>

              <p className="text-emerald-100 font-medium text-sm leading-relaxed">
                Extreme heatwaves, erratic monsoons and depleting groundwater
                are disproportionately affecting rural farmers, children and
                urban poor communities.
              </p>
            </motion.article>
          </div>
        </section>

        {/* 3. THE GROUND REALITY */}
        <section
          aria-labelledby="urgent-need-heading"
          className="max-w-7xl mx-auto px-6 lg:px-8 py-16"
        >
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <div className="flex items-center gap-3">
                <span className="w-12 h-[2px] bg-emerald-600"></span>
                <span className="text-emerald-700 font-bold text-xs uppercase tracking-widest">
                  The Urgent Need
                </span>
              </div>

              <h2
                id="urgent-need-heading"
                className="text-3xl md:text-4xl font-black text-slate-900 leading-[1.2]"
              >
                Why planting trees is no longer just an option
              </h2>

              <p className="text-slate-600 leading-relaxed font-medium">
                Urbanization and rapid deforestation have turned many areas into
                concrete heat islands. Trees are among our most effective
                defenses against rising temperatures, air pollution and
                depleting groundwater levels.
              </p>

              <p className="text-slate-600 leading-relaxed font-medium">
                Climate change is not just an environmental issue; it is a human
                crisis. Droughts and extreme weather hit poor and vulnerable
                communities the hardest, affecting livelihoods, health and food
                security.
              </p>

              <div className="bg-orange-50 border-l-4 border-orange-500 p-6 rounded-r-2xl mt-8">
                <p className="text-slate-800 font-bold italic">
                  “SEVAA Foundation&apos;s plantation drives aim to restore
                  ecological balance by involving local communities not just in
                  planting, but in nurturing native tree species.”
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative h-[500px] rounded-3xl overflow-hidden shadow-2xl"
            >
              <img
                src="https://jaipuriamba.edu.in/wp-content/uploads/2024/06/WhatsApp-Image-2024-06-04-at-7.23.34-PM-1.jpeg"
                alt="Community tree plantation drive by SEVAA Foundation"
                className="w-full h-full object-cover"
                loading="lazy"
              />

              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent flex items-end p-8">
                <p className="text-white font-medium text-lg leading-snug">
                  Turning barren lands into green hope through community
                  participation and environmental awareness.
                </p>
              </div>
            </motion.div>
          </div>
        </section>

        {/* 4. SEVAA'S INTERVENTION */}
        <section
          aria-labelledby="tree-strategy-heading"
          className="bg-white py-24 border-t border-slate-200 mt-10"
        >
          <div className="max-w-[90rem] mx-auto px-6 lg:px-8">
            <div className="text-center mb-16 max-w-3xl mx-auto">
              <h2
                id="tree-strategy-heading"
                className="text-3xl md:text-4xl font-black text-slate-900 mb-4"
              >
                How SEVAA Restores Nature
              </h2>

              <p className="text-slate-500 font-medium text-lg">
                We go beyond planting. SEVAA Foundation focuses on survival
                rates, community ownership, native species and environmental
                education.
              </p>
            </div>

            <motion.div
              variants={staggerContainerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 auto-rows-fr"
            >
              <motion.article
                variants={gridItemVariants}
                className="relative rounded-[2rem] overflow-hidden shadow-xl group cursor-pointer min-h-[350px]"
              >
                <img
                  src="https://joegardener.com/wp-content/uploads/2025/03/IMG_7193-708X466.jpg"
                  alt="Urban forest and Miyawaki plantation by SEVAA Foundation"
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  loading="lazy"
                />

                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300"></div>
              </motion.article>

              <motion.article
                variants={gridItemVariants}
                className="bg-slate-50 p-8 rounded-[2rem] shadow-lg border border-slate-100 flex flex-col justify-center hover:-translate-y-2 hover:shadow-2xl hover:shadow-emerald-500/10 hover:border-emerald-200 transition-all duration-300 group min-h-[350px]"
              >
                <div className="w-14 h-14 bg-white rounded-2xl shadow-sm flex items-center justify-center mb-6 group-hover:bg-emerald-50 transition-colors duration-300">
                  <TreePine className="w-7 h-7 text-emerald-600" />
                </div>

                <h3 className="text-xl font-bold text-slate-900 mb-3">
                  Urban Forests
                </h3>

                <p className="text-slate-600 font-medium text-sm leading-relaxed">
                  Using the Miyawaki method to create dense, fast-growing native
                  mini-forests in urban areas to fight air pollution, heat and
                  biodiversity loss.
                </p>
              </motion.article>

              <motion.article
                variants={gridItemVariants}
                className="relative rounded-[2rem] overflow-hidden shadow-xl group cursor-pointer min-h-[350px]"
              >
                <img
                  src="https://images.hindustantimes.com/rf/image_size_960x540/HT/p2/2018/08/01/Pictures/asian-little-girl-helping-tree-father-plant_35e101fe-957d-11e8-81b1-0a49ed9acdc8.jpg"
                  alt="Sapling distribution and tree planting support by SEVAA Foundation"
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  loading="lazy"
                />

                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300"></div>
              </motion.article>

              <motion.article
                variants={gridItemVariants}
                className="bg-slate-50 p-8 rounded-[2rem] shadow-lg border border-slate-100 flex flex-col justify-center hover:-translate-y-2 hover:shadow-2xl hover:shadow-orange-500/10 hover:border-orange-200 transition-all duration-300 group min-h-[350px]"
              >
                <div className="w-14 h-14 bg-white rounded-2xl shadow-sm flex items-center justify-center mb-6 group-hover:bg-orange-50 transition-colors duration-300">
                  <Leaf className="w-7 h-7 text-orange-500" />
                </div>

                <h3 className="text-xl font-bold text-slate-900 mb-3">
                  Sapling Distribution
                </h3>

                <p className="text-slate-600 font-medium text-sm leading-relaxed">
                  Distributing free fruit-bearing and shade-giving saplings to
                  rural farmers, schools and communities for long-term
                  ecological benefits.
                </p>
              </motion.article>

              <motion.article
                variants={gridItemVariants}
                className="relative rounded-[2rem] overflow-hidden shadow-xl group cursor-pointer min-h-[350px]"
              >
                <img
                  src="https://indiamedtoday.com/wp-content/uploads/2026/03/Glaucoma-Awareness-Week-Photograph-3.png"
                  alt="Environmental awareness drive and youth campaign by SEVAA Foundation"
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  loading="lazy"
                />

                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300"></div>
              </motion.article>

              <motion.article
                variants={gridItemVariants}
                className="bg-slate-50 p-8 rounded-[2rem] shadow-lg border border-slate-100 flex flex-col justify-center hover:-translate-y-2 hover:shadow-2xl hover:shadow-emerald-500/10 hover:border-emerald-200 transition-all duration-300 group min-h-[350px]"
              >
                <div className="w-14 h-14 bg-white rounded-2xl shadow-sm flex items-center justify-center mb-6 group-hover:bg-emerald-50 transition-colors duration-300">
                  <Globe className="w-7 h-7 text-emerald-600" />
                </div>

                <h3 className="text-xl font-bold text-slate-900 mb-3">
                  Awareness Drives
                </h3>

                <p className="text-slate-600 font-medium text-sm leading-relaxed">
                  Conducting workshops and campaigns to educate citizens, youth
                  and schools about climate change, waste management and nature
                  protection.
                </p>
              </motion.article>

              <motion.article
                variants={gridItemVariants}
                className="relative rounded-[2rem] overflow-hidden shadow-xl group cursor-pointer min-h-[350px]"
              >
                <img
                  src="https://m.media-amazon.com/images/I/713kOXrLz2L.jpg"
                  alt="Join SEVAA Foundation tree plantation and green India campaign"
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  loading="lazy"
                />

                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300"></div>
              </motion.article>

              <motion.article
                variants={gridItemVariants}
                className="bg-emerald-900 p-8 rounded-[2rem] shadow-lg flex flex-col justify-center hover:-translate-y-2 hover:shadow-2xl hover:shadow-emerald-900/40 transition-all duration-300 min-h-[350px] relative overflow-hidden"
              >
                <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-emerald-800 rounded-full blur-3xl pointer-events-none"></div>

                <div className="w-14 h-14 bg-emerald-800 rounded-2xl flex items-center justify-center mb-6 relative z-10">
                  <Handshake className="w-7 h-7 text-orange-400" />
                </div>

                <h3 className="text-xl font-bold text-white mb-3 relative z-10">
                  Be The Change
                </h3>

                <p className="text-emerald-100 font-medium text-sm leading-relaxed mb-6 relative z-10">
                  Whether you want to volunteer, collaborate as a school or
                  fulfil corporate CSR, SEVAA Foundation has a way for you to
                  contribute to a greener future.
                </p>

                <a
                  href="/contact"
                  className="inline-flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bold px-6 py-2.5 rounded-full transition-colors text-sm w-fit relative z-10"
                  aria-label="Join SEVAA Foundation tree plantation mission"
                >
                  Join Us
                  <Globe className="w-4 h-4 text-white/80" />
                </a>
              </motion.article>
            </motion.div>
          </div>
        </section>

      </main>
    </>
  );
};

export default TreePlantation;